import { Command as CommandPrimitive, CommandSeparator as CommandSeparatorPrimitive } from 'cmdk';
import { SearchIcon } from 'lucide-react';
import { type ComponentProps, type ReactElement } from 'react';
import { cn } from '@avanta/shared/utils/cn';
import { Dialog } from '../Dialog';
import { commandStyles } from './styles';

const Command = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive>): ReactElement => (
  <CommandPrimitive
    data-slot="command"
    className={cn(commandStyles.command, className)}
    {...props}
  />
);

const CommandDialog = ({
  title,
  description,
  children,
  className,
  ...props
}: ComponentProps<typeof Dialog> & {
  title?: string;
  description?: string;
  className?: string;
  showCloseButton?: boolean;
}): ReactElement => (
  <Dialog title={title} description={description} className={className} {...props}>
    <div className={cn(commandStyles.commandDialog, className)}>
      <Command className={commandStyles.commandDialogCommand}>{children}</Command>
    </div>
  </Dialog>
);

const CommandInput = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Input>): ReactElement => (
  <div
    data-slot="command-input-wrapper"
    className={cn(commandStyles.commandInputWrapper, className)}
  >
    <SearchIcon className="size-4 shrink-0 opacity-50" />
    <CommandPrimitive.Input
      data-slot="command-input"
      className={cn(commandStyles.commandInput, className)}
      {...props}
    />
  </div>
);

const CommandList = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.List>): ReactElement => (
  <CommandPrimitive.List
    data-slot="command-list"
    className={cn(commandStyles.commandList, className)}
    {...props}
  />
);

const CommandEmpty = ({
  ...props
}: ComponentProps<typeof CommandPrimitive.Empty>): ReactElement => (
  <CommandPrimitive.Empty
    data-slot="command-empty"
    className={commandStyles.commandEmpty}
    {...props}
  />
);

const CommandGroup = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Group>): ReactElement => (
  <CommandPrimitive.Group
    data-slot="command-group"
    className={cn(commandStyles.commandGroup, className)}
    {...props}
  />
);

const CommandSeparator = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Separator>): ReactElement => (
  <CommandSeparatorPrimitive
    data-slot="command-separator"
    className={cn(commandStyles.commandSeparator, className)}
    {...props}
  />
);

const CommandItem = ({
  className,
  ...props
}: ComponentProps<typeof CommandPrimitive.Item>): ReactElement => (
  <CommandPrimitive.Item
    data-slot="command-item"
    className={cn(commandStyles.commandItem, className)}
    {...props}
  />
);

const CommandShortcut = ({ className, ...props }: ComponentProps<'span'>): ReactElement => (
  <span
    data-slot="command-shortcut"
    className={cn(commandStyles.commandShortcut, className)}
    {...props}
  />
);

export {
  Command,
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
